import { buildDigitalTwinIndex } from "../domain/digital-twin-index";
import { ASSET_BY_ID } from "../domain/assets";
import { resolveLayerIdForObjectType } from "../domain/layers";
import {
  deriveDefaultEquipmentId,
  generateChildIndexCode,
  generateObjectIndexCode,
} from "../domain/indexing";
import type {
  AssetDefinition,
  EquipmentRecord,
  InventoryItem,
  Project,
  Room,
  Scene,
  SceneObject,
  StorageLocation,
} from "../domain/schema";
import type { SceneCommand } from "../domain/history";
import {
  applyOrganizationCommand,
  inventoryCreationCommand,
} from "../domain/inventory-organization";
import { useEditorStore } from "../store/editor-store";
import type {
  AgentMoveReviewResult,
  LabSpaceStagingActions,
  PendingAgentChange,
  PendingAgentInventoryChange,
  PendingAgentLayoutChange,
  PendingAgentMoveChange,
  PendingAgentResizeChange,
  PendingAgentWorkspaceChange,
  PlannedWallSegment,
  StageRoomLayoutResult,
  StageInventoryPlanResult,
  StageObjectMoveResult,
  StageObjectResizeResult,
} from "./labspace-action-types";
import { LabSpaceActionError } from "./labspace-read-actions";
import { validateObjectMove, validateObjectResize } from "./labspace-spatial-actions";
import { agentActivityActions } from "./agent-activity-store";
import { getStoredRoomPlan } from "./labspace-layout-actions";
import { getStoredInventoryPlan } from "./labspace-inventory-actions";
import {
  hostOpeningAtPoint,
  projectPointToWall,
  resolveHostedOpening,
} from "../domain/wall-openings";
import {
  consumeInitialRoomPlanAutoCommit,
  isInitialRoomPlanAutoCommitEligible,
  registerInitialRoomPlanCapability,
} from "./labspace-workspace-actions";
import { decideWebMcpMutation } from "./webmcp-execution-policy";

function resolveObject(project: Project, objectId: string): { room: Room; object: SceneObject } {
  for (const room of project.rooms) {
    if (room.roomKind === "demo-template") continue;
    const object = room.scene.objects.find((entry) => entry.id === objectId);
    if (object) return { room, object };
  }
  throw new LabSpaceActionError("Object not found in the current LabSpace project.");
}

function sameObject(left: SceneObject | undefined, right: SceneObject) {
  return Boolean(left && JSON.stringify(left) === JSON.stringify(right));
}

function sameProposal(
  pending: PendingAgentMoveChange,
  objectId: string,
  target: { xMm: number; yMm: number; zMm: number; rotationDeg: number },
) {
  return (
    pending.objectId === objectId &&
    pending.proposed.position.x === target.xMm &&
    pending.proposed.position.y === target.yMm &&
    pending.proposed.position.z === target.zMm &&
    pending.proposed.rotation.z === target.rotationDeg
  );
}

function sameResizeProposal(
  pending: PendingAgentResizeChange,
  objectId: string,
  proposed: { widthMm: number; depthMm: number; heightMm: number },
) {
  return (
    pending.objectId === objectId &&
    pending.proposed.dimensions.width === proposed.widthMm &&
    pending.proposed.dimensions.depth === proposed.depthMm &&
    pending.proposed.dimensions.height === proposed.heightMm
  );
}

function activeLaboratoryCode(project: Project, room: Room) {
  return (
    project.laboratories.find((laboratory) => laboratory.id === room.laboratoryId)?.code ?? "LAB"
  );
}

export function createEquipmentRecord(
  object: SceneObject,
  existing: EquipmentRecord[],
): EquipmentRecord {
  return {
    id: crypto.randomUUID(),
    objectId: object.id,
    equipmentId: deriveDefaultEquipmentId(object, existing),
    name: object.name,
    manufacturer: "",
    model: "",
    serialNumber: "",
    status: "active",
    responsiblePerson: "",
    lastServiceDate: null,
    nextServiceDate: null,
    powerRequirements: "",
    waterRequirements: "None",
    gasRequirements: "None",
    drainRequired: false,
    ventilationRequired: false,
    notes: "Added through an approved LabSpace room plan.",
  };
}

export function createStorageLocations(
  definition: AssetDefinition,
  object: SceneObject,
  roomId: string,
  now: string,
) {
  const root: StorageLocation = {
    id: crypto.randomUUID(),
    roomId,
    objectId: object.id,
    parentId: null,
    type: "cabinet",
    name: object.name,
    indexCode: object.indexCode,
    order: 0,
    capacityNotes: "",
    childIds: [],
    createdAt: now,
    updatedAt: now,
  };
  const locations: StorageLocation[] = [root];
  const byTemplateKey = new Map<string, StorageLocation>();
  for (const entry of definition.storageTemplate ?? []) {
    const parent = entry.parentKey ? byTemplateKey.get(entry.parentKey) : root;
    if (!parent) continue;
    const location: StorageLocation = {
      id: crypto.randomUUID(),
      roomId,
      objectId: object.id,
      parentId: parent.id,
      type: entry.type,
      name: entry.name,
      indexCode: generateChildIndexCode(parent, entry.type, locations),
      order: locations.filter(
        (candidate) => candidate.parentId === parent.id && candidate.type === entry.type,
      ).length,
      capacityNotes: entry.capacityNotes ?? "",
      childIds: [],
      normalizedBounds: entry.normalizedBounds,
      anatomyKey: entry.anatomyKey,
      createdAt: now,
      updatedAt: now,
    };
    parent.childIds.push(location.id);
    locations.push(location);
    byTemplateKey.set(entry.key, location);
  }
  object.childLocationIds = [root.id];
  return locations;
}

function instantiatePlanObject(
  project: Project,
  room: Room,
  scene: Scene,
  planId: string,
  proposal: ReturnType<typeof getStoredRoomPlan>["result"]["proposals"][number],
  hostWallIds: Map<string, string>,
) {
  const definition = ASSET_BY_ID.get(proposal.assetId);
  if (!definition)
    throw new LabSpaceActionError(`Catalog asset is unavailable: ${proposal.assetId}.`);
  const now = new Date().toISOString();
  const object: SceneObject = {
    id: crypto.randomUUID(),
    indexCode: generateObjectIndexCode(
      room,
      scene,
      definition.objectType,
      room.scene.zones[0]?.id ?? null,
      activeLaboratoryCode(project, room),
    ),
    name: definition.name,
    assetDefinitionId: definition.id,
    objectType: definition.objectType,
    position: {
      x: proposal.position.xMm,
      y: proposal.position.yMm,
      z: proposal.position.zMm,
    },
    dimensions: proposal.dimensionsMm,
    rotation: { x: 0, y: 0, z: proposal.rotationDeg },
    flipHorizontal: false,
    flipVertical: false,
    layerId: resolveLayerIdForObjectType(scene.layers, definition.objectType),
    roomId: room.id,
    zoneId: room.scene.zones[0]?.id ?? null,
    locked: true,
    visible: true,
    metadata: {
      agentPlanPreview: true,
      agentPlanId: planId,
      agentPlanPlacement: proposal.placement,
    },
    createdAt: now,
    updatedAt: now,
    parentObjectId: null,
    childLocationIds: [],
    zIndex: Math.max(0, ...scene.objects.map((entry) => entry.zIndex)) + 1,
  };
  if (proposal.opening) {
    const hostWallId = hostWallIds.get(proposal.opening.hostWallProposalId);
    const hostWall = scene.objects.find((entry) => entry.id === hostWallId && entry.wall);
    if (!hostWall?.wall) {
      throw new LabSpaceActionError(`The hosted wall for ${definition.name} is unavailable.`);
    }
    const length = Math.hypot(
      hostWall.wall.end.x - hostWall.wall.start.x,
      hostWall.wall.end.y - hostWall.wall.start.y,
    );
    const ratio = proposal.opening.offsetMm / length;
    const target = {
      x: hostWall.wall.start.x + (hostWall.wall.end.x - hostWall.wall.start.x) * ratio,
      y: hostWall.wall.start.y + (hostWall.wall.end.y - hostWall.wall.start.y) * ratio,
    };
    const projection = projectPointToWall(hostWall, target, object.dimensions.width);
    if (!projection) {
      throw new LabSpaceActionError(`${definition.name} no longer fits its planned host wall.`);
    }
    object.opening = {
      wallId: hostWall.id,
      offset: projection.offset,
      width: object.dimensions.width,
      sillHeight: proposal.opening.sillHeightMm,
      height: object.dimensions.height,
      handing: proposal.opening.handing,
      swing: proposal.opening.swing,
    };
    Object.assign(object, hostOpeningAtPoint(object, projection));
  }
  return { definition, object, now };
}

function instantiatePlanWall(
  project: Project,
  room: Room,
  scene: Scene,
  planId: string,
  segment: PlannedWallSegment,
) {
  const definition = ASSET_BY_ID.get("straight-wall");
  if (!definition) throw new LabSpaceActionError("The canonical wall definition is unavailable.");
  const now = new Date().toISOString();
  const start = { x: segment.start.xMm, y: segment.start.yMm };
  const end = { x: segment.end.xMm, y: segment.end.yMm };
  const object: SceneObject = {
    id: crypto.randomUUID(),
    indexCode: generateObjectIndexCode(
      room,
      scene,
      "wall",
      null,
      activeLaboratoryCode(project, room),
    ),
    name: segment.name,
    assetDefinitionId: definition.id,
    objectType: "wall",
    position: {
      x: (start.x + end.x) / 2,
      y: (start.y + end.y) / 2,
      z: 0,
    },
    dimensions: {
      width: segment.lengthMm,
      depth: segment.thicknessMm,
      height: segment.heightMm,
    },
    rotation: {
      x: 0,
      y: 0,
      z: (Math.atan2(end.y - start.y, end.x - start.x) * 180) / Math.PI,
    },
    flipHorizontal: false,
    flipVertical: false,
    layerId: resolveLayerIdForObjectType(scene.layers, "wall"),
    roomId: room.id,
    zoneId: null,
    locked: true,
    visible: true,
    metadata: {
      agentPlanPreview: true,
      agentPlanId: planId,
      agentPlanShell: true,
    },
    createdAt: now,
    updatedAt: now,
    parentObjectId: null,
    childLocationIds: [],
    zIndex: Math.max(0, ...scene.objects.map((entry) => entry.zIndex)) + 1,
    wall: {
      start,
      end,
      thickness: segment.thicknessMm,
      height: segment.heightMm,
      halfHeight: false,
    },
  };
  return object;
}

function stagedResult(change: PendingAgentMoveChange): StageObjectMoveResult {
  return {
    staged: true,
    stageId: change.stageId,
    objectId: change.objectId,
    objectName: change.objectName,
    proposed: change.validation.target,
    valid: true,
    persisted: false,
    requiresHumanApproval: true,
    conflicts: [],
  };
}

function invalidResult(validation: ReturnType<typeof validateObjectMove>): StageObjectMoveResult {
  return {
    staged: false,
    stageId: null,
    objectId: validation.objectId,
    objectName: validation.objectName,
    proposed: validation.target,
    valid: false,
    persisted: false,
    requiresHumanApproval: false,
    conflicts: validation.conflicts,
  };
}

export function stageObjectMove(input: unknown): StageObjectMoveResult {
  const validation = validateObjectMove(input);
  if (!validation.valid) return invalidResult(validation);

  const state = useEditorStore.getState();
  const existing = state.pendingAgentChange;
  if (existing) {
    if (
      existing.tool === "move" &&
      sameProposal(existing, validation.objectId, validation.target)
    ) {
      return stagedResult(existing);
    }
    throw new LabSpaceActionError(
      "Another agent change is awaiting human review. Approve or cancel it before staging a new move.",
    );
  }
  if (state.saveStatus !== "saved") {
    throw new LabSpaceActionError(
      "LabSpace must finish saving current human edits before an agent move can be staged.",
    );
  }

  const { room, object } = resolveObject(state.project, validation.objectId);
  const stageId = crypto.randomUUID();
  const record = buildDigitalTwinIndex(state.project).find(
    (entry) => entry.roomId === room.id && entry.objectId === object.id,
  );
  const focused = state.applySpatialFocus({
    requestId: crypto.randomUUID(),
    recordId: record?.id ?? `agent-stage:${stageId}`,
    roomId: room.id,
    objectId: object.id,
    locationId: null,
    showStorageAccess: false,
  });
  if (!focused) {
    throw new LabSpaceActionError("LabSpace could not present this move for review.");
  }

  const before = structuredClone(object);
  useEditorStore.getState().previewObject(object.id, {
    position: {
      ...object.position,
      x: validation.target.xMm,
      y: validation.target.yMm,
      z: validation.target.zMm,
    },
    rotation: { ...object.rotation, z: validation.target.rotationDeg },
  });
  const previewState = useEditorStore.getState();
  const previewRoom = previewState.project.rooms.find((entry) => entry.id === room.id)!;
  const proposed = structuredClone(
    previewRoom.scene.objects.find((entry) => entry.id === object.id)!,
  );
  const change: PendingAgentMoveChange = {
    stageId,
    tool: "move",
    roomId: room.id,
    objectId: object.id,
    objectName: object.name,
    objectIndexCode: object.indexCode,
    before,
    proposed,
    validation,
    baselineDirtyRevision: previewState.dirtyRevision,
    createdAt: new Date().toISOString(),
    status: "pending",
    timestamps: {
      projectUpdatedAt: state.project.updatedAt,
      roomUpdatedAt: room.updatedAt,
      sceneUpdatedAt: room.scene.updatedAt,
    },
  };
  useEditorStore.setState({ pendingAgentChange: change });
  return stagedResult(change);
}

function stagedResizeResult(change: PendingAgentResizeChange): StageObjectResizeResult {
  return {
    staged: true,
    stageId: change.stageId,
    objectId: change.objectId,
    objectName: change.objectName,
    current: change.validation.current,
    proposed: change.validation.proposed,
    valid: true,
    persisted: false,
    requiresHumanApproval: true,
    conflicts: [],
  };
}

function invalidResizeResult(
  validation: ReturnType<typeof validateObjectResize>,
): StageObjectResizeResult {
  return {
    staged: false,
    stageId: null,
    objectId: validation.objectId,
    objectName: validation.objectName,
    current: validation.current,
    proposed: validation.proposed,
    valid: false,
    persisted: false,
    requiresHumanApproval: false,
    conflicts: validation.conflicts,
  };
}

export function stageObjectResize(input: unknown): StageObjectResizeResult {
  const validation = validateObjectResize(input);
  if (!validation.valid) return invalidResizeResult(validation);

  const state = useEditorStore.getState();
  const existing = state.pendingAgentChange;
  if (existing) {
    if (
      existing.tool === "resize" &&
      sameResizeProposal(existing, validation.objectId, validation.proposed)
    ) {
      return stagedResizeResult(existing);
    }
    throw new LabSpaceActionError(
      "Another agent change is awaiting human review. Approve or cancel it before staging a new resize.",
    );
  }
  if (state.saveStatus !== "saved") {
    throw new LabSpaceActionError(
      "LabSpace must finish saving current human edits before an agent resize can be staged.",
    );
  }

  const { room, object } = resolveObject(state.project, validation.objectId);
  const stageId = crypto.randomUUID();
  const record = buildDigitalTwinIndex(state.project).find(
    (entry) => entry.roomId === room.id && entry.objectId === object.id,
  );
  const focused = state.applySpatialFocus({
    requestId: crypto.randomUUID(),
    recordId: record?.id ?? `agent-resize:${stageId}`,
    roomId: room.id,
    objectId: object.id,
    locationId: null,
    showStorageAccess: false,
  });
  if (!focused) throw new LabSpaceActionError("LabSpace could not present this resize for review.");

  const before = structuredClone(object);
  let candidate: SceneObject = {
    ...object,
    dimensions: {
      width: validation.proposed.widthMm,
      depth: validation.proposed.depthMm,
      height: validation.proposed.heightMm,
    },
    ...(object.opening
      ? {
          opening: {
            ...object.opening,
            width: validation.proposed.widthMm,
            height: validation.proposed.heightMm,
          },
        }
      : {}),
  };
  if (candidate.opening) {
    const projection = resolveHostedOpening(candidate, room.scene.objects);
    if (!projection) {
      throw new LabSpaceActionError("The hosted opening could not be resolved on its wall.");
    }
    candidate = { ...candidate, ...hostOpeningAtPoint(candidate, projection) };
  }
  useEditorStore.getState().previewObject(object.id, {
    dimensions: candidate.dimensions,
    ...(candidate.opening
      ? {
          position: candidate.position,
          rotation: candidate.rotation,
          opening: candidate.opening,
        }
      : {}),
  });
  const previewState = useEditorStore.getState();
  const previewRoom = previewState.project.rooms.find((entry) => entry.id === room.id)!;
  const proposed = structuredClone(
    previewRoom.scene.objects.find((entry) => entry.id === object.id)!,
  );
  const change: PendingAgentResizeChange = {
    stageId,
    tool: "resize",
    roomId: room.id,
    objectId: object.id,
    objectName: object.name,
    objectIndexCode: object.indexCode,
    before,
    proposed,
    validation,
    baselineDirtyRevision: previewState.dirtyRevision,
    createdAt: new Date().toISOString(),
    status: "pending",
    timestamps: {
      projectUpdatedAt: state.project.updatedAt,
      roomUpdatedAt: room.updatedAt,
      sceneUpdatedAt: room.scene.updatedAt,
    },
  };
  useEditorStore.setState({ pendingAgentChange: change });
  return stagedResizeResult(change);
}

function normalizeStageRoomLayoutInput(input: unknown) {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new LabSpaceActionError("Tool input must be a JSON object.");
  }
  const record = input as Record<string, unknown>;
  const unexpected = Object.keys(record).find((key) => key !== "planId");
  if (unexpected) throw new LabSpaceActionError(`Unexpected input field: ${unexpected}.`);
  if (typeof record.planId !== "string" || !record.planId.trim()) {
    throw new LabSpaceActionError("planId must be a non-empty string.");
  }
  return { planId: record.planId.trim() };
}

function layoutStageResult(
  change: PendingAgentLayoutChange,
  execution = decideWebMcpMutation("existing-room-layout", { valid: true }),
): StageRoomLayoutResult {
  const wallCount = change.proposedObjects.filter((object) => object.kind === "wall").length;
  return {
    staged: true,
    stageId: change.stageId,
    planId: change.planId,
    roomId: change.roomId,
    roomName: change.roomName,
    objectCount: change.proposedObjects.length,
    wallCount,
    assetCount: change.proposedObjects.length - wallCount,
    floorGenerated: wallCount > 0,
    objects: change.proposedObjects,
    persisted: false,
    requiresHumanApproval: execution.disposition === "review-required",
    autoCommitted: execution.disposition === "fast-applied",
    executionMode: execution.mode,
    executionDisposition: execution.disposition,
    executionReason: execution.reason,
  };
}

export function stageRoomLayout(input: unknown): StageRoomLayoutResult {
  const { planId } = normalizeStageRoomLayoutInput(input);
  const stored = getStoredRoomPlan(planId);
  if (!stored.result.proposals.length && stored.result.shell.mode !== "proposed") {
    throw new LabSpaceActionError("That room plan contains no placeable objects or room shell.");
  }
  const state = useEditorStore.getState();
  const existing = state.pendingAgentChange;
  if (existing) {
    if (existing.tool === "layout" && existing.planId === planId)
      return layoutStageResult(
        existing,
        decideWebMcpMutation("existing-room-layout", { valid: true }),
      );
    throw new LabSpaceActionError(
      "Another agent change is awaiting human review. Approve or cancel it before staging a room plan.",
    );
  }
  if (state.saveStatus !== "saved") {
    throw new LabSpaceActionError(
      "LabSpace must finish saving current human edits before a room plan can be staged.",
    );
  }
  const room = state.project.rooms.find((entry) => entry.id === stored.result.roomId);
  if (!room || room.roomKind === "demo-template" || room.id !== state.project.activeRoomId) {
    throw new LabSpaceActionError("Open the room used for this plan before staging it.");
  }
  const currentObjectIds = room.scene.objects.map((object) => object.id).sort();
  if (
    room.scene.updatedAt !== stored.baseline.sceneUpdatedAt ||
    JSON.stringify(currentObjectIds) !== JSON.stringify(stored.baseline.objectIds) ||
    JSON.stringify({ width: room.width, depth: room.depth, wallHeight: room.wallHeight }) !==
      JSON.stringify(stored.baseline.roomSize)
  ) {
    throw new LabSpaceActionError(
      "The room changed after this plan was calculated. Create a fresh plan before staging.",
    );
  }

  const initialLayoutEligible = isInitialRoomPlanAutoCommitEligible(
    room.id,
    state.dirtyRevision,
    room,
  );
  const completeBlueprint =
    stored.result.unplaced.length === 0 &&
    stored.result.plannedObjects === stored.result.requestedObjects;
  const execution = decideWebMcpMutation(
    initialLayoutEligible ? "initial-room-blueprint" : "existing-room-layout",
    { valid: true, pristine: initialLayoutEligible, complete: completeBlueprint },
  );

  const stageId = crypto.randomUUID();
  const beforeScene = structuredClone(room.scene);
  const proposedScene = structuredClone(room.scene);
  const beforeSpaces = structuredClone(room.spaces);
  const primarySpaceId =
    room.spaces.find((space) => space.kind === "primary")?.id ?? room.spaces[0]?.id;
  const proposedObjects: PendingAgentLayoutChange["proposedObjects"] = [];
  const proposedObjectIds: string[] = [];
  const hostWallIds = new Map<string, string>();
  room.scene.objects.forEach((object) => {
    if (object.wall) hostWallIds.set(object.id, object.id);
  });
  if (stored.result.shell.mode === "proposed") {
    for (const segment of stored.result.shell.segments) {
      const wall = instantiatePlanWall(state.project, room, proposedScene, planId, segment);
      wall.spaceId = primarySpaceId;
      proposedScene.objects.push(wall);
      hostWallIds.set(segment.proposalId, wall.id);
      proposedObjectIds.push(wall.id);
      proposedObjects.push({
        objectId: wall.id,
        name: wall.name,
        indexCode: wall.indexCode,
        kind: "wall",
        position: {
          xMm: wall.position.x,
          yMm: wall.position.y,
          zMm: wall.position.z,
          rotationDeg: wall.rotation.z,
        },
      });
    }
  }
  for (const proposal of stored.result.proposals) {
    const { definition, object, now } = instantiatePlanObject(
      state.project,
      room,
      proposedScene,
      planId,
      proposal,
      hostWallIds,
    );
    object.spaceId = primarySpaceId;
    proposedScene.objects.push(object);
    if (definition.indexingBehavior === "storage") {
      proposedScene.storageLocations.push(
        ...createStorageLocations(definition, object, room.id, now).map((location) => ({
          ...location,
          spaceId: primarySpaceId,
        })),
      );
    }
    if (definition.objectType === "equipment") {
      proposedScene.equipmentRecords.push({
        ...createEquipmentRecord(object, proposedScene.equipmentRecords),
        spaceId: primarySpaceId,
      });
    }
    proposedObjectIds.push(object.id);
    proposedObjects.push({
      objectId: object.id,
      name: object.name,
      indexCode: object.indexCode,
      kind: "asset",
      position: {
        xMm: object.position.x,
        yMm: object.position.y,
        zMm: object.position.z,
        rotationDeg: object.rotation.z,
      },
    });
  }
  proposedScene.updatedAt = new Date().toISOString();
  const proposedSpaces = structuredClone(room.spaces);
  if (stored.result.shell.mode === "proposed") {
    const wallIds = proposedScene.objects
      .filter((object) => object.wall)
      .map((object) => object.id);
    const primary = proposedSpaces.find((space) => space.id === primarySpaceId);
    if (primary) primary.wallIds = wallIds;
  }
  const change: PendingAgentLayoutChange = {
    stageId,
    tool: "layout",
    planId,
    roomId: room.id,
    roomName: room.name,
    brief: stored.result.brief,
    changeKind: "room-plan",
    beforeScene,
    proposedScene: structuredClone(proposedScene),
    beforeSpaces,
    proposedSpaces: structuredClone(proposedSpaces),
    proposedObjectIds,
    proposedObjects,
    beforeRoomSize: { width: room.width, depth: room.depth, wallHeight: room.wallHeight },
    proposedRoomSize: {
      width: stored.result.shell.widthMm,
      depth: stored.result.shell.depthMm,
      wallHeight: stored.result.shell.wallHeightMm,
    },
    baselineDirtyRevision: state.dirtyRevision,
    createdAt: new Date().toISOString(),
    status: "pending",
    timestamps: {
      projectUpdatedAt: state.project.updatedAt,
      roomUpdatedAt: room.updatedAt,
      sceneUpdatedAt: room.scene.updatedAt,
    },
  };
  const now = new Date().toISOString();
  useEditorStore.setState({
    project: {
      ...state.project,
      updatedAt: now,
      rooms: state.project.rooms.map((entry) =>
        entry.id === room.id
          ? {
              ...entry,
              width: stored.result.shell.widthMm,
              depth: stored.result.shell.depthMm,
              wallHeight: stored.result.shell.wallHeightMm,
              spaces: proposedSpaces,
              updatedAt: now,
              scene: proposedScene,
            }
          : entry,
      ),
    },
    selectedIds: proposedObjectIds,
    pendingAgentChange: change,
  });
  if (execution.disposition === "fast-applied") {
    commitLayout(change, "automatic");
    return layoutStageResult(change, execution);
  }
  return layoutStageResult(change, execution);
}

export function stageInventoryPlan(input: unknown): StageInventoryPlanResult {
  const { planId } = normalizeStageRoomLayoutInput(input);
  const stored = getStoredInventoryPlan(planId);
  const state = useEditorStore.getState();
  if (state.pendingAgentChange) {
    if (
      state.pendingAgentChange.tool === "inventory" &&
      state.pendingAgentChange.planId === planId
    ) {
      const pending = state.pendingAgentChange;
      return {
        staged: true,
        stageId: pending.stageId,
        planId,
        entryCount: pending.entries.length,
        assignedEntries: pending.entries.filter((entry) => entry.storageLocationId).length,
        persisted: false,
        requiresHumanApproval: true,
      };
    }
    throw new LabSpaceActionError(
      "Another agent change is awaiting human review. Approve or cancel it before staging inventory.",
    );
  }
  if (state.saveStatus !== "saved") {
    throw new LabSpaceActionError(
      "LabSpace must finish saving current human edits before inventory can be staged.",
    );
  }
  if (
    state.project.updatedAt !== stored.baseline.projectUpdatedAt ||
    Object.entries(stored.baseline.roomUpdatedAt).some(
      ([roomId, updatedAt]) =>
        state.project.rooms.find((room) => room.id === roomId)?.updatedAt !== updatedAt,
    )
  ) {
    throw new LabSpaceActionError(
      "The project changed after this inventory plan was calculated. Create a fresh plan before staging.",
    );
  }
  const change: PendingAgentInventoryChange = {
    stageId: crypto.randomUUID(),
    tool: "inventory",
    planId,
    entries: structuredClone(stored.result.entries),
    baselineDirtyRevision: state.dirtyRevision,
    createdAt: new Date().toISOString(),
    status: "pending",
    projectUpdatedAt: state.project.updatedAt,
  };
  useEditorStore.setState({ pendingAgentChange: change });
  return {
    staged: true,
    stageId: change.stageId,
    planId,
    entryCount: change.entries.length,
    assignedEntries: change.entries.filter((entry) => entry.storageLocationId).length,
    persisted: false,
    requiresHumanApproval: true,
  };
}

function requirePending(stageId: string): PendingAgentChange {
  const pending = useEditorStore.getState().pendingAgentChange;
  if (!pending || pending.stageId !== stageId) {
    throw new LabSpaceActionError("That staged change is no longer awaiting review.");
  }
  return pending;
}

function cancelMove(pending: PendingAgentMoveChange): AgentMoveReviewResult {
  const stageId = pending.stageId;
  const state = useEditorStore.getState();
  const room = state.project.rooms.find((entry) => entry.id === pending.roomId);
  const current = room?.scene.objects.find((entry) => entry.id === pending.objectId);

  if (room && sameObject(current, pending.proposed)) {
    useEditorStore.setState({
      project: {
        ...state.project,
        updatedAt: pending.timestamps.projectUpdatedAt,
        rooms: state.project.rooms.map((entry) =>
          entry.id === room.id
            ? {
                ...entry,
                updatedAt: pending.timestamps.roomUpdatedAt,
                scene: {
                  ...entry.scene,
                  updatedAt: pending.timestamps.sceneUpdatedAt,
                  objects: entry.scene.objects.map((object) =>
                    object.id === pending.objectId ? pending.before : object,
                  ),
                },
              }
            : entry,
        ),
      },
      pendingAgentChange: null,
    });
  } else {
    useEditorStore.setState({ pendingAgentChange: null });
  }
  useEditorStore.getState().pushToast("Agent move cancelled. The layout was not saved.", "info");
  agentActivityActions.record({
    actor: "Human",
    action: "Move rejected",
    subject: pending.objectName,
    status: "rejected",
    evidence: "Preview removed · project data unchanged",
  });
  return {
    stageId,
    objectId: pending.objectId,
    status: "cancelled",
    persisted: false,
  };
}

function approveMove(pending: PendingAgentMoveChange): AgentMoveReviewResult {
  const stageId = pending.stageId;
  const state = useEditorStore.getState();
  const room = state.project.rooms.find((entry) => entry.id === pending.roomId);
  const current = room?.scene.objects.find((entry) => entry.id === pending.objectId);
  if (
    state.dirtyRevision !== pending.baselineDirtyRevision ||
    !sameObject(current, pending.proposed)
  ) {
    cancelStagedObjectMove(stageId);
    throw new LabSpaceActionError(
      "The staged move became stale because the layout changed. It was not committed.",
    );
  }

  useEditorStore.setState({ pendingAgentChange: null });
  useEditorStore.getState().commitPreview(pending.before, "Approve agent move");
  useEditorStore
    .getState()
    .pushToast("Agent move approved. LabSpace is saving the change.", "success");
  agentActivityActions.record({
    actor: "Human",
    action: "Move approved",
    subject: pending.objectName,
    status: "approved",
    evidence: "Explicit researcher approval",
  });
  agentActivityActions.record({
    actor: "LabSpace",
    action: "Change committed",
    subject: pending.objectName,
    status: "committed",
    evidence: "One history entry · Undo available",
  });
  return {
    stageId,
    objectId: pending.objectId,
    status: "approved",
    persisted: false,
  };
}

function cancelResize(pending: PendingAgentResizeChange): AgentMoveReviewResult {
  const state = useEditorStore.getState();
  const room = state.project.rooms.find((entry) => entry.id === pending.roomId);
  const current = room?.scene.objects.find((entry) => entry.id === pending.objectId);
  if (room && sameObject(current, pending.proposed)) {
    useEditorStore.setState({
      project: {
        ...state.project,
        updatedAt: pending.timestamps.projectUpdatedAt,
        rooms: state.project.rooms.map((entry) =>
          entry.id === room.id
            ? {
                ...entry,
                updatedAt: pending.timestamps.roomUpdatedAt,
                scene: {
                  ...entry.scene,
                  updatedAt: pending.timestamps.sceneUpdatedAt,
                  objects: entry.scene.objects.map((object) =>
                    object.id === pending.objectId ? pending.before : object,
                  ),
                },
              }
            : entry,
        ),
      },
      pendingAgentChange: null,
    });
  } else {
    useEditorStore.setState({ pendingAgentChange: null });
  }
  useEditorStore.getState().pushToast("Agent resize cancelled. The layout was not saved.", "info");
  agentActivityActions.record({
    actor: "Human",
    action: "Resize rejected",
    subject: pending.objectName,
    status: "rejected",
    evidence: "Preview removed · project data unchanged",
  });
  return {
    stageId: pending.stageId,
    objectId: pending.objectId,
    status: "cancelled",
    persisted: false,
  };
}

function approveResize(pending: PendingAgentResizeChange): AgentMoveReviewResult {
  const state = useEditorStore.getState();
  const room = state.project.rooms.find((entry) => entry.id === pending.roomId);
  const current = room?.scene.objects.find((entry) => entry.id === pending.objectId);
  if (
    state.dirtyRevision !== pending.baselineDirtyRevision ||
    !sameObject(current, pending.proposed)
  ) {
    cancelResize(pending);
    throw new LabSpaceActionError(
      "The staged resize became stale because the layout changed. It was not committed.",
    );
  }
  useEditorStore.setState({ pendingAgentChange: null });
  useEditorStore.getState().commitPreview(pending.before, "Approve agent resize");
  useEditorStore
    .getState()
    .pushToast("Agent resize approved. LabSpace is saving the change.", "success");
  agentActivityActions.record({
    actor: "Human",
    action: "Resize approved",
    subject: pending.objectName,
    status: "approved",
    evidence: "Explicit researcher approval",
  });
  agentActivityActions.record({
    actor: "LabSpace",
    action: "Change committed",
    subject: pending.objectName,
    status: "committed",
    evidence: "One history entry · Undo available",
  });
  return {
    stageId: pending.stageId,
    objectId: pending.objectId,
    status: "approved",
    persisted: false,
  };
}

function cancelLayout(pending: PendingAgentLayoutChange): AgentMoveReviewResult {
  const state = useEditorStore.getState();
  const room = state.project.rooms.find((entry) => entry.id === pending.roomId);
  if (
    room &&
    JSON.stringify(room.scene) === JSON.stringify(pending.proposedScene) &&
    JSON.stringify(room.spaces) === JSON.stringify(pending.proposedSpaces) &&
    room.width === pending.proposedRoomSize.width &&
    room.depth === pending.proposedRoomSize.depth &&
    room.wallHeight === pending.proposedRoomSize.wallHeight
  ) {
    useEditorStore.setState({
      project: {
        ...state.project,
        updatedAt: pending.timestamps.projectUpdatedAt,
        rooms: state.project.rooms.map((entry) =>
          entry.id === pending.roomId
            ? {
                ...entry,
                ...pending.beforeRoomSize,
                spaces: pending.beforeSpaces,
                updatedAt: pending.timestamps.roomUpdatedAt,
                scene: pending.beforeScene,
              }
            : entry,
        ),
      },
      selectedIds: [],
      pendingAgentChange: null,
    });
  } else {
    useEditorStore.setState({ pendingAgentChange: null });
  }
  useEditorStore
    .getState()
    .pushToast(
      pending.changeKind === "annex"
        ? "Annex preview cancelled. The room was not changed."
        : "Agent room plan cancelled. The room was not changed.",
      "info",
    );
  agentActivityActions.record({
    actor: "Human",
    action: pending.changeKind === "annex" ? "Annex rejected" : "Room plan rejected",
    subject: `${pending.proposedObjects.length} proposed room elements`,
    status: "rejected",
    evidence: "Blueprint preview removed · project data unchanged",
    correlationId: pending.planId,
    roomId: pending.roomId,
  });
  return {
    stageId: pending.stageId,
    objectId: pending.proposedObjectIds[0] ?? pending.planId,
    objectIds: pending.proposedObjectIds,
    status: "cancelled",
    persisted: false,
  };
}

function committedLayoutScene(pending: PendingAgentLayoutChange, scene: Scene) {
  const proposedIds = new Set(pending.proposedObjectIds);
  return {
    ...scene,
    updatedAt: new Date().toISOString(),
    objects: scene.objects.map((object) => {
      if (!proposedIds.has(object.id)) return object;
      const metadata = { ...object.metadata };
      delete metadata.agentPlanPreview;
      delete metadata.agentPlanId;
      delete metadata.agentPlanPlacement;
      delete metadata.agentPlanShell;
      return { ...object, locked: false, metadata, updatedAt: new Date().toISOString() };
    }),
  };
}

function commitLayout(
  pending: PendingAgentLayoutChange,
  mode: "human" | "automatic",
): AgentMoveReviewResult {
  const state = useEditorStore.getState();
  const room = state.project.rooms.find((entry) => entry.id === pending.roomId);
  if (
    !room ||
    state.dirtyRevision !== pending.baselineDirtyRevision ||
    JSON.stringify(room.scene) !== JSON.stringify(pending.proposedScene) ||
    JSON.stringify(room.spaces) !== JSON.stringify(pending.proposedSpaces) ||
    room.width !== pending.proposedRoomSize.width ||
    room.depth !== pending.proposedRoomSize.depth ||
    room.wallHeight !== pending.proposedRoomSize.wallHeight
  ) {
    if (mode === "human") cancelLayout(pending);
    else useEditorStore.setState({ pendingAgentChange: null });
    throw new LabSpaceActionError(
      "The staged room plan became stale because the layout changed. It was not committed.",
    );
  }
  const after = committedLayoutScene(pending, room.scene);
  const command: SceneCommand = {
    id: crypto.randomUUID(),
    roomId: pending.roomId,
    label:
      mode === "automatic"
        ? `Create initial WebMCP room plan (${pending.proposedObjects.length} elements)`
        : pending.changeKind === "annex"
          ? `Approve annex (${pending.proposedObjects.length} elements)`
          : `Approve agent room plan (${pending.proposedObjects.length} elements)`,
    kind: "scene",
    before: pending.beforeScene,
    after,
    roomBefore: { ...pending.beforeRoomSize, spaces: pending.beforeSpaces },
    roomAfter: { ...pending.proposedRoomSize, spaces: pending.proposedSpaces },
  };
  const now = new Date().toISOString();
  useEditorStore.setState({
    project: {
      ...state.project,
      updatedAt: now,
      rooms: state.project.rooms.map((entry) =>
        entry.id === pending.roomId
          ? {
              ...entry,
              ...pending.proposedRoomSize,
              spaces: pending.proposedSpaces,
              updatedAt: now,
              scene: after,
            }
          : entry,
      ),
    },
    // Keep the completed Fast Draft presentation clean; the automatic blueprint
    // should read as a finished room rather than a cyan multi-selection. A human
    // approval keeps the proposed elements selected for immediate inspection.
    selectedIds: mode === "automatic" ? [] : pending.proposedObjectIds,
    pendingAgentChange: null,
    history: [...state.history, command],
    future: [],
    saveStatus: "unsaved",
    dirtyRevision: state.dirtyRevision + 1,
  });
  if (pending.changeKind === "room-plan") {
    consumeInitialRoomPlanAutoCommit(pending.roomId);
  }
  const assetCount = pending.proposedObjects.filter((object) => object.kind === "asset").length;
  useEditorStore
    .getState()
    .pushToast(
      mode === "automatic"
        ? `Fast Draft created the initial room plan with ${assetCount} assets. Undo is available.`
        : pending.changeKind === "annex"
          ? `Annex approved. LabSpace is saving the connected space and ${assetCount} assets.`
          : `Room plan approved. LabSpace is saving the room shell and ${assetCount} assets.`,
      "success",
    );
  if (mode === "human") {
    agentActivityActions.record({
      actor: "Human",
      action: pending.changeKind === "annex" ? "Annex approved" : "Room plan approved",
      subject: `${pending.proposedObjects.length} planned elements in ${pending.roomName}`,
      status: "approved",
      evidence: "Explicit researcher approval",
      correlationId: pending.planId,
      roomId: pending.roomId,
    });
  } else {
    agentActivityActions.record({
      actor: "LabSpace",
      action: "Fast Draft room plan committed",
      subject: pending.roomName,
      status: "committed",
      evidence: "Pristine room blueprint · deterministic validation passed · Undo available",
      correlationId: pending.planId,
      roomId: pending.roomId,
    });
  }
  agentActivityActions.record({
    actor: "LabSpace",
    action: pending.changeKind === "annex" ? "Annex committed" : "Layout committed",
    subject: pending.roomName,
    status: "committed",
    evidence: "Objects and index records committed as one history entry · Undo available",
    correlationId: pending.planId,
    roomId: pending.roomId,
  });
  return {
    stageId: pending.stageId,
    objectId: pending.proposedObjectIds[0] ?? pending.planId,
    objectIds: pending.proposedObjectIds,
    status: "approved",
    persisted: false,
  };
}

function approveLayout(pending: PendingAgentLayoutChange): AgentMoveReviewResult {
  return commitLayout(pending, "human");
}

function cancelWorkspace(pending: PendingAgentWorkspaceChange): AgentMoveReviewResult {
  useEditorStore.setState({ pendingAgentChange: null });
  useEditorStore.getState().pushToast("Room creation cancelled. No room was created.", "info");
  agentActivityActions.record({
    actor: "Human",
    action: "Room creation rejected",
    subject: `${pending.roomName} · ${pending.roomCode}`,
    status: "rejected",
    evidence: "Proposal dismissed · project data unchanged",
  });
  return {
    stageId: pending.stageId,
    objectId: pending.roomCode,
    status: "cancelled",
    persisted: false,
  };
}

function approveWorkspace(pending: PendingAgentWorkspaceChange): AgentMoveReviewResult {
  const state = useEditorStore.getState();
  const laboratory = state.project.laboratories.find((entry) => entry.id === pending.laboratoryId);
  const duplicate = state.project.rooms.some(
    (room) =>
      room.laboratoryId === pending.laboratoryId &&
      room.code.toLowerCase() === pending.roomCode.toLowerCase(),
  );
  if (
    state.project.id !== pending.projectId ||
    state.dirtyRevision !== pending.baselineDirtyRevision ||
    state.project.updatedAt !== pending.projectUpdatedAt ||
    !laboratory ||
    duplicate
  ) {
    cancelWorkspace(pending);
    throw new LabSpaceActionError(
      "The room proposal became stale because the workspace changed. It was not created.",
    );
  }

  useEditorStore.setState({ pendingAgentChange: null });
  const roomId = useEditorStore.getState().createRoom({
    laboratoryId: pending.laboratoryId,
    name: pending.roomName,
    code: pending.roomCode,
  });
  if (!roomId) {
    throw new LabSpaceActionError("LabSpace could not create the approved room.");
  }
  useEditorStore.getState().updateRoomFacilityPlacement(roomId, { floor: pending.floor - 1 });
  registerInitialRoomPlanCapability(roomId);
  void useEditorStore.getState().saveNow();
  useEditorStore
    .getState()
    .pushToast(`Created ${pending.roomName}. The empty room is being saved.`, "success");
  agentActivityActions.record({
    actor: "Human",
    action: "Room creation approved",
    subject: `${pending.roomName} · ${pending.roomCode}`,
    status: "approved",
    evidence: "Explicit researcher approval",
  });
  agentActivityActions.record({
    actor: "LabSpace",
    action: "Empty room created",
    subject: `${pending.laboratoryCode} · Floor ${pending.floor}`,
    status: "committed",
    evidence: "Additive workspace change · local save requested",
  });
  return {
    stageId: pending.stageId,
    objectId: roomId,
    status: "approved",
    persisted: false,
  };
}

function cancelInventory(pending: PendingAgentInventoryChange): AgentMoveReviewResult {
  useEditorStore.setState({ pendingAgentChange: null });
  useEditorStore
    .getState()
    .pushToast("Agent inventory plan cancelled. No records were created.", "info");
  agentActivityActions.record({
    actor: "Human",
    action: "Inventory plan rejected",
    subject: `${pending.entries.length} proposed records`,
    status: "rejected",
    evidence: "Review dismissed · project data unchanged",
  });
  return {
    stageId: pending.stageId,
    objectId: pending.planId,
    objectIds: pending.entries.map((entry) => entry.itemId),
    status: "cancelled",
    persisted: false,
  };
}

function approveInventory(pending: PendingAgentInventoryChange): AgentMoveReviewResult {
  const state = useEditorStore.getState();
  if (
    state.dirtyRevision !== pending.baselineDirtyRevision ||
    state.project.updatedAt !== pending.projectUpdatedAt
  ) {
    cancelInventory(pending);
    throw new LabSpaceActionError(
      "The staged inventory plan became stale because the project changed. It was not committed.",
    );
  }
  const now = new Date().toISOString();
  const entriesByRoom = new Map<string, PendingAgentInventoryChange["entries"]>();
  pending.entries.forEach((entry) =>
    entriesByRoom.set(entry.roomId, [...(entriesByRoom.get(entry.roomId) ?? []), entry]),
  );
  const command = inventoryCreationCommand(
    pending.entries.map((entry) => ({
      roomId: entry.roomId,
      item: {
        id: entry.itemId,
        name: entry.name,
        quantity: entry.quantity,
        unit: entry.unit,
        notes: entry.notes,
        owner: entry.owner,
        expiryDate: entry.expiryDate,
        storageLocationId: entry.storageLocationId,
        createdAt: now,
        updatedAt: now,
      } satisfies InventoryItem,
    })),
  );
  const project = applyOrganizationCommand(state.project, command, "apply");
  useEditorStore.setState({
    project,
    pendingAgentChange: null,
    history: [...state.history, command],
    future: [],
    saveStatus: "unsaved",
    dirtyRevision: state.dirtyRevision + 1,
  });
  useEditorStore
    .getState()
    .pushToast(`${pending.entries.length} approved inventory records are being saved.`, "success");
  agentActivityActions.record({
    actor: "Human",
    action: "Inventory plan approved",
    subject: `${pending.entries.length} records`,
    status: "approved",
    evidence: "Explicit researcher approval",
  });
  agentActivityActions.record({
    actor: "LabSpace",
    action: "Inventory committed",
    subject: `${entriesByRoom.size} room${entriesByRoom.size === 1 ? "" : "s"}`,
    status: "committed",
    evidence: "Canonical records created in selected room locations · one Undo available",
  });
  return {
    stageId: pending.stageId,
    objectId: pending.planId,
    objectIds: pending.entries.map((entry) => entry.itemId),
    status: "approved",
    persisted: false,
  };
}

export function cancelStagedChange(stageId: string): AgentMoveReviewResult {
  const pending = requirePending(stageId);
  return pending.tool === "workspace"
    ? cancelWorkspace(pending)
    : pending.tool === "layout"
      ? cancelLayout(pending)
      : pending.tool === "inventory"
        ? cancelInventory(pending)
        : pending.tool === "resize"
          ? cancelResize(pending)
          : cancelMove(pending);
}

export function approveStagedChange(stageId: string): AgentMoveReviewResult {
  const pending = requirePending(stageId);
  return pending.tool === "workspace"
    ? approveWorkspace(pending)
    : pending.tool === "layout"
      ? approveLayout(pending)
      : pending.tool === "inventory"
        ? approveInventory(pending)
        : pending.tool === "resize"
          ? approveResize(pending)
          : approveMove(pending);
}

export function cancelStagedObjectMove(stageId: string): AgentMoveReviewResult {
  const pending = requirePending(stageId);
  if (pending.tool !== "move") throw new LabSpaceActionError("That staged change is a room plan.");
  return cancelMove(pending);
}

export function approveStagedObjectMove(stageId: string): AgentMoveReviewResult {
  const pending = requirePending(stageId);
  if (pending.tool !== "move") throw new LabSpaceActionError("That staged change is a room plan.");
  return approveMove(pending);
}

export const labSpaceStagingActions: LabSpaceStagingActions = {
  stageObjectMove,
  stageObjectResize,
  stageRoomLayout,
  stageInventoryPlan,
  approveStagedObjectMove,
  cancelStagedObjectMove,
  approveStagedChange,
  cancelStagedChange,
};
